'use client';

import * as Tabs from '@radix-ui/react-tabs';
import { Buildings } from 'phosphor-icons';
import { useLayoutEffect, useRef, useState } from 'react';
import { c } from '../../../utils/tailwind-merge';
import { Avatar } from '../../atoms/Avatar/Avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../atoms/Tooltip/TooltipRadix';

import type {
  AccountTypeTabsProps,
  AccountTypeTabsValue,
} from './AccountTypeTabs.types';

type TruncatedTextWithTooltipProps = {
  text: string;
};

const TruncatedTextWithTooltip = ({ text }: TruncatedTextWithTooltipProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
  }, [text]);

  const reference = (
    <span
      className="block w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-left text-xs font-normal leading-[18px] tracking-[-0.024px] text-grey-400"
      ref={ref}
    >
      [{text}]
    </span>
  );

  if (!isTruncated) {
    return reference;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{reference}</TooltipTrigger>
      <TooltipContent
        arrowClassName="fill-grey-700"
        className="z-[60] max-w-[250px] break-words rounded-sm bg-grey-700 px-4 py-3 text-xs text-white"
      >
        [{text}]
      </TooltipContent>
    </Tooltip>
  );
};

type CustomTabTriggerProps = {
  value: string;
  ariaLabel: string;
  isSelected: boolean;
  label: string;
  truncatedText: string;
  avatar: React.ReactNode;
};

const CustomTabTrigger = ({
  value,
  ariaLabel,
  isSelected,
  label,
  truncatedText,
  avatar,
}: CustomTabTriggerProps) => {
  return (
    <Tabs.Trigger
      value={value}
      aria-label={ariaLabel}
      data-test={`account-type-tab-${value}`}
      className={c(
        'relative flex w-1/2 flex-shrink-0 cursor-pointer items-center gap-3 pb-2 pl-3 pr-2 pt-2',
        'transition-colors focus-visible:outline-none focus-visible:ring-2',
        'focus-visible:ring-blue focus-visible:ring-offset-2 focus-visible:ring-offset-grey-900',
        'border-b-2',
        isSelected ? 'border-blue' : 'border-grey-700 hover:border-grey-500'
      )}
    >
      {avatar}
      <div className="flex min-w-0 flex-1 flex-col items-start">
        <span
          className={c(
            'block w-full truncate text-left text-base font-normal leading-6 tracking-tighter',
            !isSelected ? 'text-grey-400' : 'text-grey-100'
          )}
        >
          {label}
        </span>
        <TruncatedTextWithTooltip text={truncatedText} />
      </div>
    </Tabs.Trigger>
  );
};

export const AccountTypeTabs = ({
  userName,
  userProfilePicture,
  publisherName,
  value,
  microcopy,
  onValueChange,
  className = '',
}: AccountTypeTabsProps) => {
  const handleChange = (newValue: string) => {
    if (newValue) {
      onValueChange(newValue as AccountTypeTabsValue);
    }
  };

  const isPersonalSelected = value === 'personal';
  const isOrganizationSelected = value === 'organization';

  return (
    <Tabs.Root
      value={value}
      onValueChange={handleChange}
      className={c('flex w-full', className)}
    >
      <Tabs.List
        aria-label={microcopy.ariaLabel}
        className="flex w-full border-none"
      >
        <CustomTabTrigger
          value="personal"
          ariaLabel={`${microcopy.personal}: ${userName}`}
          isSelected={isPersonalSelected}
          label={microcopy.personal}
          truncatedText={userName}
          avatar={
            <Avatar
              userName={userName}
              userProfilePicture={userProfilePicture}
              size={32}
              className={c(
                'avatar-icon hidden font-medium md:flex',
                isPersonalSelected ? 'bg-blue' : 'bg-grey-800'
              )}
              textClassName={c(
                'text-[13.333px]',
                !isPersonalSelected ? 'text-grey-400' : 'text-white'
              )}
            />
          }
        />
        <CustomTabTrigger
          value="organization"
          ariaLabel={`${microcopy.organization}: ${publisherName}`}
          isSelected={isOrganizationSelected}
          label={microcopy.organization}
          truncatedText={publisherName}
          avatar={
            <div
              className={c(
                'avatar-icon hidden h-8 w-8 items-center justify-center rounded-full transition-colors md:flex',
                isOrganizationSelected ? 'bg-blue' : 'bg-grey-800'
              )}
            >
              <Buildings
                size={16}
                weight="bold"
                aria-hidden="true"
                focusable="false"
                className={
                  !isOrganizationSelected ? 'text-grey-400' : 'text-grey-100'
                }
              />
            </div>
          }
        />
      </Tabs.List>
    </Tabs.Root>
  );
};
